/**
 * WORK-ROUTING-QA-PAGE-BUILD-DOCS-RUNBOOK-01 — the internal work-routing
 * QA page (`/dashboard/owner/work-routing-qa`).
 *
 * THIS IS A QA / TESTING SURFACE ONLY. It is NOT the production work queue
 * and NOT the June 30 demo surface. It exists so the founder can exercise
 * the PR #123 owner-scoped QA endpoints against the applied
 * routing_config / lead_actions dark schema:
 *
 *   GET  /api/work-routing/qa/routing-config
 *   GET  /api/work-routing/qa/lead-actions
 *   POST /api/work-routing/qa/route-lead   (body: { leadId })
 *
 * DARK / FLAG-GATED: the page is reachable only behind the owner nav guard,
 * and it renders a disabled state — firing NO QA request — unless the
 * server-computed `me.workRoutingQaEnabled` signal is present (which means
 * WORK_ROUTING_VISIBLE_QA_ENABLED is on in the DEPLOYED backend AND the role
 * is the owner). The flag CANNOT be flipped from the browser/phone — turning
 * it on is a deployed-env update plus an API restart/redeploy.
 *
 * SAFETY: adds NO new write path beyond the existing route-lead endpoint;
 * sends the server ONLY a leadId (never an organizationId); exposes no env
 * values, secrets, tokens, PHI, raw phone/email, or clinical fields; never
 * enables the lead-capture hook (its flag status is shown read-only). Backend
 * route auth (503 before auth + requireOwnerScope + org-scoped reads) remains
 * the security boundary — this page is UX only.
 *
 * Pure presentational bodies (`WorkRoutingQaContent` / `WorkRoutingQaDisabled`)
 * are rendered directly in tests via react-dom/server (no jsdom), mirroring
 * the LeadsWorkspacePage convention.
 */

import { useCallback, useEffect, useState } from 'react';

import {
  fetchWorkRoutingQaLeadActions,
  fetchWorkRoutingQaRoutingConfig,
  routeWorkRoutingQaLead,
} from '@/dashboard/api';
import DashboardLoadError from '@/dashboard/DashboardLoadError';
import { useDashboardMeContext } from '@/dashboard/me-context';
import {
  isWorkRoutingQaEnabledForMe,
  leadActionRowCells,
  LEAD_ACTION_COLUMNS,
  parseLeadIdInput,
  routeResultFields,
  routingConfigRowCells,
  ROUTING_CONFIG_COLUMNS,
  workRoutingQaFlagStatus,
  WORK_ROUTING_QA_TEMPLATE_CODE,
  type WorkRoutingConfigRow,
  type WorkRoutingLeadActionRow,
  type WorkRoutingQaFlagStatus,
  type WorkRoutingRouteResult,
} from '@/dashboard/owner/work-routing-qa-view';

const ROUTE_BUTTON_LABEL = `Route lead for ${WORK_ROUTING_QA_TEMPLATE_CODE}`;

/** Boolean flag-status chips (no env strings — booleans only). */
function FlagStatusPanel({
  flagStatus,
}: {
  flagStatus: WorkRoutingQaFlagStatus;
}): JSX.Element {
  return (
    <dl className="wrqa-flags" data-testid="wrqa-flags">
      <div>
        <dt>WORK_ROUTING_VISIBLE_QA_ENABLED</dt>
        <dd data-testid="wrqa-flag-visible">
          {flagStatus.visibleQaEnabled ? 'ON' : 'OFF'}
        </dd>
      </div>
    </dl>
  );
}

function SimpleTable({
  caption,
  columns,
  rows,
  emptyCopy,
  testid,
}: {
  caption: string;
  columns: readonly string[];
  rows: string[][];
  emptyCopy: string;
  testid: string;
}): JSX.Element {
  return (
    <table className="wrqa-table" data-testid={testid}>
      <caption>{caption}</caption>
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c} scope="col">
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length > 0 ? (
          rows.map((cells, i) => (
            <tr key={i}>
              {cells.map((cell, j) => (
                <td key={columns[j] ?? j}>{cell}</td>
              ))}
            </tr>
          ))
        ) : (
          <tr data-testid={`${testid}-empty`}>
            <td colSpan={columns.length}>{emptyCopy}</td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

export interface WorkRoutingQaContentProps {
  flagStatus: WorkRoutingQaFlagStatus;
  config: readonly WorkRoutingConfigRow[];
  actions: readonly WorkRoutingLeadActionRow[];
  leadIdInput: string;
  onLeadIdChange?: (value: string) => void;
  onRoute?: () => void;
  /** A route call is in flight (button disabled). */
  routing: boolean;
  /** The most recent route result, or null. */
  result: WorkRoutingRouteResult | null;
  /** A route error message, or null. */
  actionError: string | null;
  /** A config/actions load error message, or null. */
  loadError: string | null;
}

/**
 * Pure presentational body for the ENABLED state — no fetch, no context.
 * Rendered in tests via react-dom/server.
 */
export function WorkRoutingQaContent({
  flagStatus,
  config,
  actions,
  leadIdInput,
  onLeadIdChange,
  onRoute,
  routing,
  result,
  actionError,
  loadError,
}: WorkRoutingQaContentProps): JSX.Element {
  return (
    <div data-testid="wrqa-content">
      <FlagStatusPanel flagStatus={flagStatus} />

      <section className="wrqa-route" data-testid="wrqa-route">
        <h2>Route a lead</h2>
        <p className="wrqa-note">
          Routes one existing lead through <code>{WORK_ROUTING_QA_TEMPLATE_CODE}</code>.
          Only the lead id is sent; the organization and clinic are derived
          server-side from the scoped lead row. An existing open action is
          returned as-is (no duplicate is created).
        </p>
        <label className="wrqa-lead-label">
          Lead id{' '}
          <input
            type="text"
            inputMode="numeric"
            data-testid="wrqa-lead-input"
            value={leadIdInput}
            onChange={(e) => onLeadIdChange?.(e.target.value)}
          />
        </label>
        <button
          type="button"
          data-testid="wrqa-route-button"
          disabled={routing}
          onClick={() => onRoute?.()}
        >
          {routing ? 'Routing…' : ROUTE_BUTTON_LABEL}
        </button>

        {actionError !== null ? (
          <p className="wrqa-error" role="alert" data-testid="wrqa-route-error">
            {actionError}
          </p>
        ) : null}

        {result !== null ? (
          <dl className="wrqa-result" data-testid="wrqa-route-result">
            {routeResultFields(result).map((f) => (
              <div key={f.label}>
                <dt>{f.label}</dt>
                <dd data-testid={`wrqa-result-${f.label}`}>{f.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </section>

      {loadError !== null ? (
        <div className="wrqa-load-error">
          <DashboardLoadError message={loadError} />
        </div>
      ) : (
        <>
          <SimpleTable
            caption="routing_config (your organization)"
            columns={ROUTING_CONFIG_COLUMNS}
            rows={config.map(routingConfigRowCells)}
            emptyCopy="No routing_config rows for your organization yet."
            testid="wrqa-config-table"
          />
          <SimpleTable
            caption="lead_actions (your organization)"
            columns={LEAD_ACTION_COLUMNS}
            rows={actions.map(leadActionRowCells)}
            emptyCopy="No lead_actions rows for your organization yet."
            testid="wrqa-actions-table"
          />
        </>
      )}
    </div>
  );
}

/**
 * Pure presentational body for the DISABLED state. Shows the boolean flag
 * status and how to turn the surface on — without a route control and
 * without firing any request.
 */
export function WorkRoutingQaDisabled({
  flagStatus,
}: {
  flagStatus: WorkRoutingQaFlagStatus;
}): JSX.Element {
  return (
    <div data-testid="wrqa-disabled">
      <p className="wrqa-disabled-copy" role="status">
        This QA surface is unavailable. WORK_ROUTING_VISIBLE_QA_ENABLED is off
        in this environment. It cannot be turned on from the browser — it
        requires a deployed env update plus an API restart/redeploy.
      </p>
      <FlagStatusPanel flagStatus={flagStatus} />
    </div>
  );
}

/**
 * Self-fetching, fault-isolated QA page. Reachable only behind the owner
 * nav guard; renders the disabled body (and fetches nothing) unless the
 * server-computed `workRoutingQaEnabled` signal is present.
 */
export default function WorkRoutingQaPage(): JSX.Element {
  const ctx = useDashboardMeContext();
  const me = ctx?.me ?? null;
  const enabled = isWorkRoutingQaEnabledForMe(me);
  const flagStatus = workRoutingQaFlagStatus(me);

  const [config, setConfig] = useState<WorkRoutingConfigRow[]>([]);
  const [actions, setActions] = useState<WorkRoutingLeadActionRow[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [leadIdInput, setLeadIdInput] = useState('');
  const [routing, setRouting] = useState(false);
  const [result, setResult] = useState<WorkRoutingRouteResult | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    // Fail-closed: while disabled we fire NO QA request at all.
    if (!enabled) return;
    let cancelled = false;
    setLoadError(null);
    Promise.all([
      fetchWorkRoutingQaRoutingConfig(),
      fetchWorkRoutingQaLeadActions(),
    ])
      .then(([cfg, acts]) => {
        if (cancelled) return;
        setConfig(cfg);
        setActions(acts);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : 'Failed to load QA data');
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const onRoute = useCallback(() => {
    const parsed = parseLeadIdInput(leadIdInput);
    if (!parsed.ok) {
      setActionError(parsed.reason);
      setResult(null);
      return;
    }
    setRouting(true);
    setActionError(null);
    routeWorkRoutingQaLead(parsed.leadId)
      .then((r) => {
        setResult(r);
        // Refresh the actions list so the created/existing row is visible.
        return fetchWorkRoutingQaLeadActions().then((acts) => setActions(acts));
      })
      .catch((e: unknown) => {
        setActionError(e instanceof Error ? e.message : 'Route failed');
        setResult(null);
      })
      .finally(() => setRouting(false));
  }, [leadIdInput]);

  return (
    <section className="wrqa">
      <div className="page-header">
        <h1>Work-routing QA</h1>
        <p className="page-header__sub">
          Internal QA / testing surface for the owner-scoped work-routing
          endpoints. Not the production work queue and not a demo surface.
        </p>
      </div>

      {ctx?.loading ? (
        <div className="loading">Loading…</div>
      ) : enabled ? (
        <WorkRoutingQaContent
          flagStatus={flagStatus}
          config={config}
          actions={actions}
          leadIdInput={leadIdInput}
          onLeadIdChange={setLeadIdInput}
          onRoute={onRoute}
          routing={routing}
          result={result}
          actionError={actionError}
          loadError={loadError}
        />
      ) : (
        <WorkRoutingQaDisabled flagStatus={flagStatus} />
      )}
    </section>
  );
}
